import React from 'react';
import { DollarSign, Calendar, Layers } from 'lucide-react';

// Campaign objectives
export const OBJECTIVES = [
    { value: 'AWARENESS', label: 'Brand Awareness', description: 'Reach people likely to remember your ads' },
    { value: 'TRAFFIC', label: 'Traffic', description: 'Drive visits to your website or app' },
    { value: 'ENGAGEMENT', label: 'Engagement', description: 'Get more messages, video views, or post engagement' },
    { value: 'LEADS', label: 'Leads', description: 'Collect leads for your business' },
    { value: 'CONVERSIONS', label: 'Conversions', description: 'Drive valuable actions on your website or app' },
    { value: 'SALES', label: 'Sales', description: 'Find people likely to purchase your products' }
];

// Funnel stages
export const FUNNEL_STAGES = [
    { value: 'TOFU', label: 'Top of Funnel (Awareness)', color: 'var(--color-info)' },
    { value: 'MOFU', label: 'Middle of Funnel (Consideration)', color: 'var(--color-warning)' },
    { value: 'BOFU', label: 'Bottom of Funnel (Conversion)', color: 'var(--color-success)' }
];

// Platforms
export const PLATFORMS = [
    { value: 'meta', label: 'Meta (Facebook/Instagram)', icon: '📘' },
    { value: 'google', label: 'Google Ads', icon: '🔍' },
    { value: 'tiktok', label: 'TikTok', icon: '🎵' },
    { value: 'linkedin', label: 'LinkedIn', icon: '💼' }
];

/**
 * CampaignForm - Reusable form component for Campaign node editing
 * Used in both CampaignModal and PropertiesPanel
 */
const CampaignForm = ({
    formData,
    onChange,
    errors = {},
    showPlatformSelector = true
}) => {
    const handleChange = (field, value) => {
        onChange(field, value);
    };

    const handleBudgetChange = (field, value) => {
        onChange('budget', { ...formData.budget, [field]: value });
    };

    const handleScheduleChange = (field, value) => {
        onChange('schedule', { ...formData.schedule, [field]: value });
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {/* Campaign Name */}
            <div className="input-group">
                <label className="input-label">Campaign Name *</label>
                <input
                    type="text"
                    className="input-field"
                    placeholder="e.g., Summer Sale Awareness"
                    value={formData.name || ''}
                    onChange={(e) => handleChange('name', e.target.value)}
                    style={errors.name ? { borderColor: 'var(--color-error)' } : {}}
                />
                {errors.name && (
                    <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)' }}>
                        {errors.name}
                    </span>
                )}
            </div>

            {/* Platform Selection */}
            {showPlatformSelector && (
                <div className="input-group">
                    <label className="input-label">Platform *</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-2)' }}>
                        {PLATFORMS.map(platform => (
                            <button
                                key={platform.value}
                                type="button"
                                onClick={() => handleChange('platform', platform.value)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    padding: 'var(--space-3)',
                                    background: formData.platform === platform.value
                                        ? 'var(--color-primary-light)'
                                        : 'var(--color-bg-secondary)',
                                    border: formData.platform === platform.value
                                        ? '2px solid var(--color-primary)'
                                        : '1px solid var(--color-border)',
                                    borderRadius: 'var(--radius-md)',
                                    cursor: 'pointer',
                                    textAlign: 'left'
                                }}
                            >
                                <span style={{ fontSize: 'var(--font-size-lg)' }}>{platform.icon}</span>
                                <span style={{
                                    fontSize: 'var(--font-size-sm)',
                                    color: 'var(--color-text-primary)'
                                }}>
                                    {platform.label}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Objective Selection */}
            <div className="input-group">
                <label className="input-label">Campaign Objective *</label>
                <select
                    className="select-field"
                    value={formData.objective || 'AWARENESS'}
                    onChange={(e) => handleChange('objective', e.target.value)}
                >
                    {OBJECTIVES.map(obj => (
                        <option key={obj.value} value={obj.value}>
                            {obj.label}
                        </option>
                    ))}
                </select>
                <p style={{
                    margin: 'var(--space-1) 0 0 0',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-tertiary)'
                }}>
                    {OBJECTIVES.find(o => o.value === formData.objective)?.description}
                </p>
            </div>

            {/* Funnel Stage */}
            <div className="input-group">
                <label className="input-label">
                    <Layers size={14} style={{ marginRight: 'var(--space-1)' }} />
                    Funnel Stage
                </label>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    {FUNNEL_STAGES.map(stage => (
                        <button
                            key={stage.value}
                            type="button"
                            onClick={() => handleChange('funnelStage', stage.value)}
                            style={{
                                flex: 1,
                                padding: 'var(--space-2) var(--space-3)',
                                background: formData.funnelStage === stage.value
                                    ? `${stage.color}20`
                                    : 'var(--color-bg-secondary)',
                                border: formData.funnelStage === stage.value
                                    ? `2px solid ${stage.color}`
                                    : '1px solid var(--color-border)',
                                borderRadius: 'var(--radius-md)',
                                cursor: 'pointer',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: formData.funnelStage === stage.value ? 600 : 400,
                                color: formData.funnelStage === stage.value
                                    ? stage.color
                                    : 'var(--color-text-secondary)'
                            }}
                        >
                            {stage.value}
                        </button>
                    ))}
                </div>
            </div>

            {/* Budget */}
            <div className="input-group">
                <label className="input-label">
                    <DollarSign size={14} style={{ marginRight: 'var(--space-1)' }} />
                    Budget *
                </label>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    <div style={{ position: 'relative', flex: 2 }}>
                        <span style={{
                            position: 'absolute',
                            left: 'var(--space-3)',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: 'var(--color-text-tertiary)'
                        }}>
                            $
                        </span>
                        <input
                            type="number"
                            className="input-field"
                            style={{
                                paddingLeft: 'var(--space-6)',
                                borderColor: errors.budget ? 'var(--color-error)' : undefined
                            }}
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                            value={formData.budget?.amount || ''}
                            onChange={(e) => handleBudgetChange('amount', parseFloat(e.target.value) || 0)}
                        />
                    </div>
                    <select
                        className="select-field"
                        style={{ flex: 1 }}
                        value={formData.budget?.type || 'daily'}
                        onChange={(e) => handleBudgetChange('type', e.target.value)}
                    >
                        <option value="daily">Daily</option>
                        <option value="lifetime">Lifetime</option>
                    </select>
                </div>
                {errors.budget && (
                    <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)' }}>
                        {errors.budget}
                    </span>
                )}
            </div>

            {/* Schedule */}
            <div className="input-group">
                <label className="input-label">
                    <Calendar size={14} style={{ marginRight: 'var(--space-1)' }} />
                    Schedule (Optional)
                </label>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    <div style={{ flex: 1 }}>
                        <label style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-tertiary)',
                            marginBottom: 'var(--space-1)',
                            display: 'block'
                        }}>
                            Start Date
                        </label>
                        <input
                            type="date"
                            className="input-field"
                            value={formData.schedule?.start || ''}
                            onChange={(e) => handleScheduleChange('start', e.target.value)}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-tertiary)',
                            marginBottom: 'var(--space-1)',
                            display: 'block'
                        }}>
                            End Date
                        </label>
                        <input
                            type="date"
                            className="input-field"
                            value={formData.schedule?.end || ''}
                            onChange={(e) => handleScheduleChange('end', e.target.value)}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CampaignForm;
